import {useCallback, useEffect, useState} from "react";
import type {PipelineNodeStatus, PipelineStatusResponse, PipelineTargetSummary} from "@calendall/shared";
import {getPipelines, getPipelineStatus} from "./pipelinesApi";

export type PipelinesState =
        | {kind: "loading"}
        | {kind: "failed"}
        | {kind: "loaded"; targets: PipelineTargetSummary[]};

/** The pipelines this environment shows; the navigation and the Pipeline page are hidden when there are none. */
export function usePipelines(): {state: PipelinesState; reload: () => void} {
    const [state, setState] = useState<PipelinesState>({kind: "loading"});
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setState({kind: "loading"});
        getPipelines()
                .then((targets) => {
                    if (!cancelled) setState({kind: "loaded", targets});
                })
                .catch(() => {
                    if (!cancelled) setState({kind: "failed"});
                });
        return () => {
            cancelled = true;
        };
    }, [attempt]);

    const reload = useCallback(() => setAttempt((current) => current + 1), []);
    return {state, reload};
}

export const POLL_FAST_MS = 5_000;
export const POLL_IDLE_MS = 30_000;

function isActive(status: PipelineNodeStatus): boolean {
    return status === "in_progress" || status === "waiting_approval";
}

/** Polls fast while a run is in progress or waits for approval, and slowly otherwise. */
export function pipelinePollInterval(status: PipelineStatusResponse | undefined): number {
    if (!status) return POLL_IDLE_MS;
    const running = (status.execution !== null && isActive(status.execution.status))
            || status.stages.some((stage) => isActive(stage.status) || stage.actions.some((action) => isActive(action.status)));
    return running ? POLL_FAST_MS : POLL_IDLE_MS;
}

export type PipelineStatusPoll = {
    /** The last status read successfully; kept on screen when a later poll fails. */
    status: PipelineStatusResponse | undefined;
    /** The most recent poll failed. */
    failed: boolean;
    fetching: boolean;
    retry: () => void;
};

/** One pipeline's status, polled until the component unmounts; idle while no pipeline name is known. */
export function usePipelineStatus(pipelineName: string | undefined): PipelineStatusPoll {
    const [status, setStatus] = useState<PipelineStatusResponse | undefined>(undefined);
    const [failed, setFailed] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!pipelineName) return;
        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        let latest: PipelineStatusResponse | undefined;

        const poll = async (): Promise<void> => {
            setFetching(true);
            try {
                latest = await getPipelineStatus(pipelineName);
                if (cancelled) return;
                setStatus(latest);
                setFailed(false);
            } catch {
                if (cancelled) return;
                setFailed(true);
            }
            setFetching(false);
            timer = setTimeout(() => void poll(), pipelinePollInterval(latest));
        };
        void poll();

        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [pipelineName, attempt]);

    const retry = useCallback(() => setAttempt((current) => current + 1), []);
    return {status, failed, fetching, retry};
}
